import {
  AthleteTerm,
  DashboardCreatePlatform,
  DashboardFederationDetail,
  DashboardPlatformDetail,
  DashboardPlatformFeeInput,
  DashboardPlatformLegalDetails,
  DashboardPlatformListItem,
  DashboardPlatformTerminology,
  DashboardUpdatePlatformInput,
  LeagueRatingSystem,
  LeagueRatingSystemInput,
  LeagueRatingSystems,
  LeagueRatingSystemsInput,
  PlatformRef,
  PlatformStatus as ApiPlatformStatus,
  VenueTerm,
} from "@/api/__generated__/schemas";
import {
  FeeTabData,
  FeeVatFormValues,
  mapFeesSegmentsToFeeTabData,
  mapFeeVatFormValuesToInput,
  mapSubmittedFeeDataToRestPlatformFeeInputs,
  mapTournatedFeeVatToFormValues,
  SubmittedFeeData,
} from "@/components/payment";
import {
  BasePlatformFragment,
  PlatformFragment,
  PlatformWithDocumentsFragment,
  PlatformWithSubscriptionDocumentsFragment,
  PlatformWithFeesFragment,
  PlatformWithOwnerFragment,
  PlatformWithSportsFragment,
  PlatformWithThirdPartyLicensesFragment,
} from "@/graphql";
import { isLanguage, Language } from "@/i18n/i18n.types";
import { mapToBaseClub } from "@/mappers/club.mappers";
import {
  mapToCurrencyCode,
  mapToTimeZone,
  mapToCountryCodeNullable,
  mapToDateFormat,
  mapToTimeFormat,
} from "@/mappers/common.mappers";
import { mapToDiscipline } from "@/mappers/discipline.mappers";
import { mapToThirdPartyLicense, mapToThirdPartyLicenseReference } from "@/mappers/license.mappers";
import {
  mapToOrganization,
  mapToOrganizationReference,
  mapToOrganizationWithUser,
} from "@/mappers/organization.mappers";
import {
  mapToFeesSegment,
  mapToFeeType,
  mapToServiceType,
  mapToTournatedFeeVat,
} from "@/mappers/payment.mappers";
import { mapToSport, mapToSportRef } from "@/mappers/sport.mappers";
import {
  Organization,
  OrganizationManager,
  OrganizationWithManagers,
} from "@/types/organization.types";
import {
  BasePlatform,
  LegalEntityTypeValues,
  Platform,
  PlatformBrandingFileIds,
  PlatformBrandingFormValues,
  PlatformDetail,
  PlatformExtrasFormValues,
  PlatformDomainFormValues,
  PlatformGeneralFormValues,
  PlatformLegalDocumentSource,
  PlatformLegalDocumentSourceValues,
  PlatformLegalDocumentsFormValues,
  PlatformLegalFormValues,
  PlatformListItem,
  PlatformModulesIntegrationsFormValues,
  PlatformOwnerFormValues,
  PlatformRatingSystem,
  PlatformRatingSystems,
  PlatformReference,
  PlatformRegionalFormValues,
  PlatformResolvedLegalDetails,
  PlatformStatus,
  PlatformWithDocuments,
  PlatformWithFees,
  PlatformWithOwner,
  PlatformWithSports,
  PlatformWithThirdPartyLicenses,
} from "@/types/platform.types";
import { TerminologySettings } from "@/types/terminology.types";
import {
  CountryCode,
  CurrencyCode,
  type Color,
  getFullPhoneNumber,
  getLocalTimeZone,
  isDefined,
  makeFileUploadFieldState,
  makePhoneFieldState,
  toColor,
} from "@/ui";

import { mapToDocumentsAndFolders, mapToDocumentsAndFoldersFromApi } from "./document.mappers";

const MOCK_PLATFORM_OWNER: Organization & OrganizationWithManagers = {
  id: "0",
  types: ["nationalGoverningBody"],
  name: "Unknown organization",
  abbreviation: "N/A",
  about: null,
  educationalInstitutionNumber: null,
  geoScope: "national",
  contactEmail: null,
  contactPhone: null,
  website: null,
  legacyLogoUrl: null,
  country: null,
  status: "active",
  enableLicenses: false,
  enableCertificates: false,
  enableApplicationForms: false,
  logoFile: null,
  enableRankings: false,
  isClubForTeamRequired: false,
  allowNewsTagging: true,
  defaultTaggedNewsVisibility: "public",
  allowGalleryTagging: true,
  defaultTaggedGalleryVisibility: "public",
  createdAt: new Date("2020-01-01T00:00:00.000Z"),
  updatedAt: new Date("2020-01-01T00:00:00.000Z"),
  sports: [],
  mainManager: {
    id: "0",
    name: "Unknown manager",
    email: "unknown@example.com",
    phone: "Unknown phone",
    surname: "Unknown surname",
    contactEmail: null,
    contactPhone: null,
    avatarFile: null,
    legacyFileUrl: null,
  },
  mainManagerContactEmail: null,
  mainManagerContactPhone: null,
  managers: [] satisfies OrganizationManager[],
};

/**
 * Backend stores `language` and `currency` as stringified JSON arrays. Some rows are
 * double-stringified (the array was JSON.stringify'd twice on save) — a single JSON.parse
 * then returns a string, so `.map()` blows up. This helper unwraps both shapes safely.
 */
export const parseStringifiedArray = (value: string | null | undefined): string[] => {
  if (!value) return [];
  try {
    let parsed: unknown = JSON.parse(value);
    // Handle double-stringified payloads: parse once more if still a string
    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === "string")
      : [];
  } catch {
    return [];
  }
};

export const mapToBasePlatform = (fragment: BasePlatformFragment): BasePlatform => ({
  id: String(fragment?.id),
  title: fragment?.title ?? "",
  platformType: fragment?.platformType ?? null,
  status: fragment?.status ?? null,
  countries: mapPlatformCountriesToCountryCodes(fragment.country ?? ""),
  dateFormat: fragment?.platformSetting?.dateFormatPreference
    ? mapToDateFormat(fragment.platformSetting.dateFormatPreference)
    : "european",
  timeFormat: fragment?.platformSetting?.timeFormatPreference
    ? mapToTimeFormat(fragment.platformSetting.timeFormatPreference)
    : "h24",
  languages: (() => {
    const parsed = parseStringifiedArray(fragment?.language);
    return parsed.length > 0 ? parsed.map(mapPlatformLanguageToLanguage) : ["en-GB"];
  })(),
  lightLogo: fragment?.lightLogo ?? null,
  darkLogo: fragment?.darkLogo ?? null,
  lightEmblem: fragment?.lightEmblem ?? null,
  timeZone: fragment.timeZone
    ? (mapToTimeZone(fragment.timeZone) ?? getLocalTimeZone())
    : getLocalTimeZone(),
  darkEmblem: fragment?.darkEmblem ?? null,
  platformLink: fragment?.platformLink ?? null,
  favicon: fragment?.favicon ?? null,
  brandColor: fragment?.primaryColor ?? null,
  currencies: parseStringifiedArray(fragment?.currency).map(mapToCurrencyCode).filter(isDefined),
});

// TODO fix it when backend fixes a bug about platform without owner
export const mapToPlatformWithOwner = (fragment: PlatformWithOwnerFragment): PlatformWithOwner => ({
  owner: fragment.owner
    ? {
        ...mapToOrganization(fragment.owner),
        ...mapToOrganizationWithUser(fragment.owner),
      }
    : MOCK_PLATFORM_OWNER,
});

export const mapToPlatformWithSports = (
  fragment: PlatformWithSportsFragment
): PlatformWithSports => ({
  sports:
    fragment?.sports
      ?.map((fragmentSport) => (fragmentSport?.sport ? mapToSport(fragmentSport?.sport) : null))
      .filter(isDefined) ?? [],
});

export const mapToPlatform = (fragment: PlatformFragment): Platform => ({
  ...mapToBasePlatform(fragment),
  abbreviation: fragment.abbreviation ?? null,
  sports:
    fragment.sports
      ?.map((sport) => (sport.sport ? mapToSport(sport.sport) : null))
      .filter(isDefined) ?? [],
  countries: mapPlatformCountriesToCountryCodes(fragment.country ?? ""),
  isMobileAppSupported: fragment.isMobileAppSupported ?? false,
  landingLink: fragment.landingLink ?? null,
  platformLink: fragment.platformLink ?? null,
  owner: fragment.owner ? mapToOrganization(fragment.owner) : null,
  signeeRole: fragment.signeeRole ?? null,
  signeeFullName: fragment.signeeFullName ?? null,
  currency: fragment.currency ?? null,
  description: fragment.description ?? null,
  alreadyDomain: fragment.alreadyDomain ?? false,
  primaryColor: fragment.primaryColor ?? null,
  secondaryColor: fragment.secondaryColor ?? null,
  uploadFile: fragment.uploadFile ?? null,
  yourRequest: fragment.yourRequest ?? null,
  darkEmblem: fragment.darkEmblem ?? null,
  lightEmblem: fragment.lightEmblem ?? null,
  lightLogo: fragment.lightLogo ?? null,
  darkLogo: fragment.darkLogo ?? null,
  serviceProvider: fragment.serviceProvider ?? null,
  liveScoring: fragment.liveScoring ?? false,
  liveStreaming: fragment.liveStreaming ?? false,
  onlineBookingCourts: fragment.onlineBookingCourts ?? null,
  onlinePayments: fragment.onlinePayments ?? false,
  licenseMemberships: fragment.licenseMemberships ?? false,
  onlinePayment: fragment.onlinePayment ?? false,
  needIntegration: fragment.needIntegration ?? null,
  needsLanding: fragment.needLanding ?? false,
  companyEmail: fragment.companyEmail ?? null,
  fullAddress: fragment.fullAddress ?? null,
  registrationNumber: fragment.registrationNumber ?? null,
  companyName: fragment.companyName ?? null,
  domain: fragment.domain ?? null,
  hasMobileApp: fragment.mobileApp ?? false,
  isEnablePrice: fragment.isEnablePrice ?? false,
  smm: fragment.smm ?? null,
  tournatedEntryFee: fragment.tournatedEntryFee ?? null,
  partnerEntryFee: fragment.partnerEntryFee ?? null,
  tournatedLicenseFee: fragment.tournatedLicenseFee ?? null,
  partnerLicenseFee: fragment.partnerLicenseFee ?? null,
  graphicsForSmm: fragment.graphicsForSmm ?? null,
  isEnableMaintenance: fragment.isEnableMaintenance ?? null,
  passcode: fragment.passcode ?? null,
  maintenanceDescription: fragment.maintenanceDescription ?? null,
  maintenanceTitle: fragment.maintenanceTitle ?? null,
  subDomain: fragment.subDomain ?? null,
  clubs: fragment.club?.map(mapToBaseClub) ?? [],
  disciplines: fragment.disciplines?.map(mapToDiscipline) ?? [],
  thirdPartyLicenses: fragment.thirdPartyLicenses?.map(mapToThirdPartyLicense) ?? [],
  customFee:
    fragment.customFee?.map((customFeeFragment) => ({
      id: customFeeFragment.id,
      rangeStart: customFeeFragment.rangeStart ?? null,
      rangeEnd: customFeeFragment.rangeEnd ?? null,
      feeType: mapToFeeType(customFeeFragment.feeType!),
      serviceType: mapToServiceType(customFeeFragment.serviceType!),
      tournatedFee: customFeeFragment.tournatedFee ?? null,
      tournatedMinFee: customFeeFragment.tournatedMinFee ?? null,
      tournatedMaxFee: customFeeFragment.tournatedMaxFee ?? null,
      tournatedMinPercentageFee: customFeeFragment.tournatedMinPercentageFee ?? null,
      tournatedMaxPercentageFee: customFeeFragment.tournatedMaxPercentageFee ?? null,
      partnerFee: customFeeFragment.partnerFee ?? null,
      partnerMinFee: customFeeFragment.partnerMinFee ?? null,
      partnerMaxFee: customFeeFragment.partnerMaxFee ?? null,
      partnerMinPercentageFee: customFeeFragment.partnerMinPercentageFee ?? null,
      partnerMaxPercentageFee: customFeeFragment.partnerMaxPercentageFee ?? null,
      currency: customFeeFragment.currency ? mapToCurrencyCode(customFeeFragment.currency) : null,
      isMonetization: customFeeFragment.isMonetization ?? null,
      isManagerPayingServiceFee: customFeeFragment.isManagerPayingServiceFee ?? null,
    })) ?? [],
  templateId: fragment.template ? fragment.template.id : null,
  platformSetting: fragment.platformSetting
    ? {
        id: fragment.platformSetting.id,
        dateFormatPreference: fragment.platformSetting.dateFormatPreference
          ? mapToDateFormat(fragment.platformSetting.dateFormatPreference)
          : null,
        timeFormatPreference: fragment.platformSetting.timeFormatPreference
          ? mapToTimeFormat(fragment.platformSetting.timeFormatPreference)
          : null,
      }
    : null,
});

export const mapToPlatformWithThirdPartyLicenses = (
  fragment: PlatformWithThirdPartyLicensesFragment
): PlatformWithThirdPartyLicenses => ({
  thirdPartyLicenses: fragment.thirdPartyLicenses?.map(mapToThirdPartyLicense) ?? [],
});

const PLATFORM_LANGUAGE_MAP: Record<string, Language> = {
  English: "en-GB",
  "English-US": "en-US",
  "English-GB": "en-GB",
  Latvian: "lv-LV",
  Spanish: "es-ES",
  "Spanish-MX": "es-MX",
  Portuguese: "pt-PT",
  Japanese: "ja-JP",
  Estonian: "et-EE",
  French: "fr-FR",
  "Polish (Polski)": "pl-PL",
  Russian: "ru-RU",
  Croatian: "hr-HR",
  Chinese: "zh-CN",
  Italian: "it-IT",
  German: "de-DE",
  Ukrainian: "uk-UA",
  Armenian: "hy-AM",
  Greek: "el-GR",
  Korean: "ko-KR",
};

export const mapPlatformLanguageToLanguage = (platformLanguageValue: string): Language => {
  if (isLanguage(platformLanguageValue)) return platformLanguageValue;
  return PLATFORM_LANGUAGE_MAP[platformLanguageValue] ?? "en-GB";
};

// The legacy human-name stored for each locale code. Persisted by the old news/platform
// forms (e.g. news.language === "English"). Only the codes that ever had a legacy dropdown
// entry appear here; codes without one (hi-IN, ka-GE, lt-LT) fall back to the code itself.
const LANGUAGE_TO_PLATFORM_LANGUAGE_MAP: Partial<Record<Language, string>> = {
  "en-GB": "English",
  "en-US": "English-US",
  "lv-LV": "Latvian",
  "es-ES": "Spanish",
  "es-MX": "Spanish-MX",
  "pt-PT": "Portuguese",
  "ja-JP": "Japanese",
  "et-EE": "Estonian",
  "fr-FR": "French",
  "pl-PL": "Polish (Polski)",
  "ru-RU": "Russian",
  "hr-HR": "Croatian",
  "zh-CN": "Chinese",
  "it-IT": "Italian",
  "de-DE": "German",
  "uk-UA": "Ukrainian",
  "hy-AM": "Armenian",
  "el-GR": "Greek",
  "ko-KR": "Korean",
};

/**
 * Reverse of {@link mapPlatformLanguageToLanguage}: turn a locale code back into the legacy
 * human name stored on `news.language` / `platform.language`. Keeps new news posts in the same
 * format as legacy rows (and the public client), so no backfill is needed.
 */
export const mapLanguageToPlatformLanguage = (language: Language): string =>
  LANGUAGE_TO_PLATFORM_LANGUAGE_MAP[language] ?? language;

export const mapPlatformCountriesToCountryCodes = (
  platformCountriesValue: string
): CountryCode[] => {
  try {
    const parsedCountries = JSON.parse(platformCountriesValue) as string[];
    return parsedCountries?.map(mapToCountryCodeNullable).filter(Boolean) as CountryCode[];
  } catch (error) {
    console.error(`Error parsing platform countries: ${platformCountriesValue}`, error);
    return [];
  }
};

export const mapToPlatformWithFees = (fragment: PlatformWithFeesFragment): PlatformWithFees => {
  const feeSegments = fragment.customFee?.map(mapToFeesSegment) ?? [];
  const entryFees = feeSegments.filter((fee) => fee.serviceType === "entry") ?? null;
  const licenseFees = feeSegments.filter((fee) => fee.serviceType === "license") ?? null;

  return {
    tournatedLicenseFee: fragment.tournatedLicenseFee ?? 0,
    partnerLicenseFee: fragment.partnerLicenseFee ?? 0,
    fees: {
      entry: entryFees,
      license: licenseFees,
    },
  };
};

// The `platform` query resolves to `PlatformWithSubscription` while `updatePlatform` returns
// `Platform`; both fragments select the same documents/documentFolders relations.
export const mapToPlatformWithDocuments = (
  fragment: PlatformWithDocumentsFragment | PlatformWithSubscriptionDocumentsFragment
): PlatformWithDocuments => mapToDocumentsAndFolders(fragment);

// REST `DashboardPlatformDetail.terminology` → `TerminologySettings`. `null` when the platform has
// no stored config (the UI then falls back to `DEFAULT_TERMINOLOGY_SETTINGS`).
export const mapToTerminologySettings = (
  input: DashboardPlatformTerminology | null | undefined
): TerminologySettings | null => (input ? { tournament: input.tournament } : null);

const EMPTY_PLATFORM_RATING_SYSTEM: PlatformRatingSystem = {
  enabled: false,
  requireAccount: false,
  displayRating: false,
  mandatory: false,
};

const mapToPlatformRatingSystem = (system: LeagueRatingSystem | undefined): PlatformRatingSystem =>
  system
    ? {
        enabled: system.enabled,
        requireAccount: system.requireAccount,
        displayRating: system.displayRating,
        mandatory: system.mandatory,
      }
    : EMPTY_PLATFORM_RATING_SYSTEM;

// REST `LeagueRatingSystems` (platform detail) → domain `PlatformRatingSystems`.
export const mapToPlatformRatingSystems = (
  systems: LeagueRatingSystems
): PlatformRatingSystems => ({
  wtn: mapToPlatformRatingSystem(systems.wtn),
  dupr: mapToPlatformRatingSystem(systems.dupr),
  wpr: mapToPlatformRatingSystem(systems.wpr),
});

/**
 * REST document source → the domain union, defaulting to the standard Tournated
 * document. Absent is a real case: the field is optional on the wire, and the
 * backend's own column default is `default`.
 */
const mapToPlatformLegalDocumentSource = (
  source: string | null | undefined
): PlatformLegalDocumentSource =>
  PlatformLegalDocumentSourceValues.find((option) => option === source) ?? "default";

/**
 * REST `resolvedLegalDetails` → the domain shape. `null` is a real answer, not
 * just an absent field: the backend withholds the entity's legal record from a
 * user who may not read that entity.
 */
const mapToPlatformResolvedLegalDetails = (
  details: DashboardPlatformLegalDetails | null | undefined
): PlatformResolvedLegalDetails | null =>
  details
    ? {
        source: details.source === "entity" ? "entity" : "platform",
        legalEntityType:
          LegalEntityTypeValues.find((option) => option === details.legalEntityType) ?? null,
        legalName: details.legalName ?? null,
        legalSurname: details.legalSurname ?? null,
        legalEmail: details.legalEmail ?? null,
        legalPhone: details.legalPhone ?? null,
        personalIdentificationNumber: details.personalIdentificationNumber ?? null,
        legalCompanyName: details.legalCompanyName ?? null,
        registrationNumber: details.registrationNumber ?? null,
        vatNumber: details.vatNumber ?? null,
        legalCountry: details.legalCountry ? mapToCountryCodeNullable(details.legalCountry) : null,
        legalAddress: details.legalAddress ?? null,
        signerName: details.signerName ?? null,
        signerSurname: details.signerSurname ?? null,
        signerRole: details.signerRole ?? null,
        signerEmail: details.signerEmail ?? null,
      }
    : null;

// REST `DashboardPlatformDetail` (`GET /api/dashboard/v1/platforms/:id`) → `PlatformDetail`.
// Widened for the Create/Update wizard: carries every field the tabs seed from, grouped by section.
export const mapToPlatformDetail = (detail: DashboardPlatformDetail): PlatformDetail => {
  const feeSegments = (detail.fees ?? []).map(mapToFeesSegment);

  return {
    id: detail.id,
    terminology: mapToTerminologySettings(detail.terminology),

    owner: detail.owner ? mapToOrganizationReference(detail.owner) : null,
    dataOwner: detail.dataOwner ? mapToOrganizationReference(detail.dataOwner) : null,

    title: detail.title ?? "",
    type: detail.type ?? null,
    abbreviation: detail.abbreviation ?? null,
    description: detail.description ?? null,
    sportIds: (detail.sports ?? []).map((sport) => sport.sport?.id).filter(isDefined),
    sports: (detail.sports ?? [])
      .map((sport) => (sport.sport ? mapToSportRef(sport.sport) : null))
      .filter(isDefined),
    geoScope: detail.geoScope ?? null,

    countries: detail.countries ?? [],
    languages: (detail.languages ?? []).map(mapPlatformLanguageToLanguage),
    timeZone: detail.timeZone
      ? (mapToTimeZone(detail.timeZone) ?? getLocalTimeZone())
      : getLocalTimeZone(),
    currencies: detail.currencies ?? [],

    lightLogoUrl: detail.lightLogoUrl ?? null,
    lightLogoFileId: detail.lightLogoFileId ?? null,
    darkLogoUrl: detail.darkLogoUrl ?? null,
    darkLogoFileId: detail.darkLogoFileId ?? null,
    lightEmblemUrl: detail.lightEmblemUrl ?? null,
    lightEmblemFileId: detail.lightEmblemFileId ?? null,
    darkEmblemUrl: detail.darkEmblemUrl ?? null,
    darkEmblemFileId: detail.darkEmblemFileId ?? null,
    faviconUrl: detail.faviconUrl ?? null,
    faviconFileId: detail.faviconFileId ?? null,
    primaryColor: detail.primaryColor ?? null,

    legalEntityType: detail.legalEntityType ?? null,
    legalName: detail.legalName ?? null,
    legalSurname: detail.legalSurname ?? null,
    legalEmail: detail.legalEmail ?? null,
    legalPhone: detail.legalPhone ?? null,
    personalIdentificationNumber: detail.personalIdentificationNumber ?? null,
    legalCompanyName: detail.legalCompanyName ?? null,
    registrationNumber: detail.registrationNumber ?? null,
    vatNumber: detail.vatNumber ?? null,
    legalCountry: detail.legalCountry ?? null,
    legalAddress: detail.legalAddress ?? null,
    isCurrentUserSigner: detail.isCurrentUserSigner ?? false,
    signerName: detail.signerName ?? null,
    signerSurname: detail.signerSurname ?? null,
    signerRole: detail.signerRole ?? null,
    signerEmail: detail.signerEmail ?? null,

    // Legal details settings. The backend's column defaults are mirrored here so
    // an older backend (one that predates these fields) reads as "the platform
    // keeps its own identity and serves the standard Tournated documents".
    usesEntityLegalDetails: detail.usesEntityLegalDetails === true,
    resolvedLegalDetails: mapToPlatformResolvedLegalDetails(detail.resolvedLegalDetails),
    termsSource: mapToPlatformLegalDocumentSource(detail.termsSource),
    termsContent: detail.termsContent ?? "",
    privacyPolicySource: mapToPlatformLegalDocumentSource(detail.privacyPolicySource),
    privacyPolicyContent: detail.privacyPolicyContent ?? "",
    cookiePolicySource: mapToPlatformLegalDocumentSource(detail.cookiePolicySource),
    cookiePolicyContent: detail.cookiePolicyContent ?? "",

    fees: {
      entry: feeSegments.filter((fee) => fee.serviceType === "entry"),
      license: feeSegments.filter((fee) => fee.serviceType === "license"),
    },
    tournatedFeeVat: mapToTournatedFeeVat(detail),

    enableToursLeagues: detail.enableToursLeagues ?? false,
    enableRankings: detail.enableRankings ?? false,
    isChatEnabled: detail.isChatEnabled ?? false,
    onlineBookingCourts: detail.onlineBookingCourts ?? false,
    licenseMemberships: detail.licenseMemberships ?? false,
    liveScoring: detail.liveScoring ?? false,
    liveStreaming: detail.liveStreaming ?? false,

    // Extras — read defensively until `yarn rest:codegen` picks the fields up
    // from the updated platform contract. The two category flags read as off
    // when absent (matching the backend's nullable columns); user creation
    // reads as ALLOWED when absent, so a deploy gap never hides the surface.
    enableLTS10: readPlatformExtraFlag(detail, "enableLTS10"),
    enableUspaLicenses: readPlatformExtraFlag(detail, "enableUspaLicenses"),
    enableUserCreation: readPlatformExtraFlag(detail, "enableUserCreation", true),
    googleAnalyticsId: readPlatformGoogleAnalyticsId(detail),

    ratingSystems: detail.ratingSystems ? mapToPlatformRatingSystems(detail.ratingSystems) : null,
    thirdPartyLicenses: (detail.thirdPartyLicenses ?? []).map(mapToThirdPartyLicenseReference),
    sporfieMode: detail.sporfieMode ?? null,
    sporfieKey: detail.liveStreaming_sporfie_key ?? null,

    alreadyDomain: detail.alreadyDomain ?? false,
    domain: detail.domain ?? null,
    subDomain: detail.subDomain ?? null,
    platformLink: detail.platformLink ?? null,
    landingLink: detail.landingLink ?? null,

    status: detail.status ? mapToPlatformStatusFromApi(detail.status) : "active",

    ...mapToDocumentsAndFoldersFromApi({
      documents: detail.documents,
      documentFolders: detail.documentFolders,
    }),
  };
};

export const mapToPlatformReference = (input: PlatformRef): PlatformReference => ({
  id: input.id,
  title: input.title ?? "",
  logo: input.logo ?? null,
  hasConnections: input.hasConnections ?? null,
  link: input.link ?? null,
});

const PLATFORM_STATUS_FROM_API: { [K in ApiPlatformStatus]: PlatformStatus } = {
  active: "active",
  inactive: "inactive",
};

export const mapToPlatformStatusFromApi = (status: ApiPlatformStatus): PlatformStatus =>
  PLATFORM_STATUS_FROM_API[status];

export const mapToApiPlatformStatus = (status: PlatformStatus): ApiPlatformStatus =>
  status === "inactive" ? ApiPlatformStatus.inactive : ApiPlatformStatus.active;

// `TerminologySettings` (form/domain) → REST `DashboardPlatformTerminology` (write payload).
// Only the tournament term is configurable for now; athlete/venue are sent at their API defaults
// so the (still-required) backend contract is satisfied without exposing those terms in the UI.
export const mapToApiTerminology = (
  settings: TerminologySettings
): DashboardPlatformTerminology => ({
  tournament: settings.tournament,
  athlete: AthleteTerm.athlete,
  venue: VenueTerm.venue,
});

// REST `DashboardPlatformListItem` (`GET /api/dashboard/v1/platforms`) → `PlatformListItem`.
export const mapToPlatformListItem = (item: DashboardPlatformListItem): PlatformListItem => ({
  id: item.id,
  title: item.title ?? "",
  abbreviation: item.abbreviation ?? null,
  logo: item.logo ?? null,
  status: item.status ? mapToPlatformStatusFromApi(item.status) : "active",
  owner: item.owner ? mapToOrganizationReference(item.owner) : null,
  dataOwner: item.dataOwner ? mapToOrganizationReference(item.dataOwner) : null,
  sports: item.sports.map(mapToSportRef),
  // `usersCount` / `entriesCount` are suppressed to `null` per row when the
  // caller has no `USER:READ` on that platform (DEV-4512); the money fields and
  // their currency likewise when the caller has no `PLATFORM_FINANCE:READ`
  // (DEV-4502/DEV-4512). `null` must survive the mapping — coercing it to `0`
  // (or a default currency) would show a suppressed figure as a real one.
  usersCount: item.usersCount ?? null,
  leaguesCount: item.leaguesCount ?? 0,
  tournamentsCount: item.tournamentsCount ?? 0,
  issuedLicensesCount: item.issuedLicensesCount ?? 0,
  issuedCertificatesCount: item.issuedCertificatesCount ?? 0,
  issuedApplicationFormsCount: item.issuedApplicationFormsCount ?? 0,
  entriesCount: item.entriesCount ?? null,
  transactionsCount: item.transactionsCount ?? 0,
  revenue: item.revenue ?? null,
  generatedPlatformFees: item.generatedPlatformFees ?? null,
  currency: item.targetCurrency ? mapToCurrencyCode(item.targetCurrency) : null,
  createdAt: new Date(item.createdAt),
  updatedAt: new Date(item.updatedAt),
});

// ===========================================================================
// Create / Update Platform wizard (C1) — the form-values ⇄ DTO contract.
//
// INBOUND: `PlatformDetail` → per-section form values (C3 update tabs seed from these).
// OUTBOUND: section form values → the create DTO (`buildCreatePlatformInput`) and
//           per-section PATCH slices (`build<Section>UpdateSlice`).
//
// Uploading is NOT done here: the page awaits its uploads and passes the resolved
// storage file ids in (`PlatformBrandingFileIds`). RAW date/time preference enums
// are used verbatim (never the display-only `DateFormat` / `TimeFormat`).
// ===========================================================================

/**
 * Platform fee form/submitted contracts, reusing the shared `@/components/payment`
 * fee shapes (platforms carry both an ENTRY and a LICENSE tab, unlike leagues).
 */
export type PlatformFeesFormValues = {
  entry: FeeTabData;
  license: FeeTabData;
  /** Platform-level VAT on the Tournated fee — one setting for both tabs. */
  vat: FeeVatFormValues;
};
export type PlatformFeesSubmittedValues = {
  entry: SubmittedFeeData;
  license: SubmittedFeeData;
  vat: FeeVatFormValues;
};

// --- shared field mappers ---------------------------------------------------

/**
 * `ColorPickerFormField` holds a React Aria `Color`; `toString("hex")` serializes
 * it to `#RRGGBB` — the shape the backend's `/^#[0-9A-Fa-f]{6}$/` accepts.
 */
const serializeBrandingColor = (color: Color): string => toColor(color).toString("hex");

/** Colour for the CREATE DTO: the primary hex when custom, omitted otherwise. */
const mapPlatformColorsToCreate = (
  values: PlatformBrandingFormValues
): Pick<DashboardCreatePlatform, "primaryColor"> =>
  values.useCustomColorPalette ? { primaryColor: serializeBrandingColor(values.primaryColor) } : {};

/** Colour for the UPDATE DTO: the primary hex when custom, `null` (clear) otherwise. */
const mapPlatformColorsToUpdate = (
  values: PlatformBrandingFormValues
): Pick<DashboardUpdatePlatformInput, "primaryColor"> =>
  values.useCustomColorPalette
    ? { primaryColor: serializeBrandingColor(values.primaryColor) }
    : { primaryColor: null };

/** Domain `PlatformRatingSystems` → REST `LeagueRatingSystemsInput` (whole-object replace). */
export const mapPlatformRatingSystemsToInput = (
  systems: PlatformRatingSystems
): LeagueRatingSystemsInput => {
  const toInput = (system: PlatformRatingSystem): LeagueRatingSystemInput => ({
    enabled: system.enabled,
    requireAccount: system.requireAccount,
    displayRating: system.displayRating,
    mandatory: system.mandatory,
  });

  return {
    wtn: toInput(systems.wtn),
    dupr: toInput(systems.dupr),
    wpr: toInput(systems.wpr),
  };
};

/** Submitted ENTRY + LICENSE fee tabs → the flat REST fee-row array. */
export const mapPlatformFeesToInput = (
  submitted: PlatformFeesSubmittedValues
): DashboardPlatformFeeInput[] => [
  ...mapSubmittedFeeDataToRestPlatformFeeInputs(submitted.entry, "entry"),
  ...mapSubmittedFeeDataToRestPlatformFeeInputs(submitted.license, "license"),
];

/**
 * Legal fields for the CREATE DTO: the active branch's fields, opposite-branch
 * fields omitted. The signer is always the named person, so `isCurrentUserSigner`
 * is sent as a fixed `false` (the BE keeps the column) and the signer* fields ship.
 */
const mapPlatformLegalToCreateFields = (
  values: PlatformLegalFormValues
): Partial<DashboardCreatePlatform> => {
  if (values.legalEntityType === "individual") {
    const { individual } = values;
    return {
      legalEntityType: "individual",
      legalName: individual.name || undefined,
      legalSurname: individual.surname || undefined,
      legalCountry: individual.residence ?? undefined,
      personalIdentificationNumber: individual.personalNumber || undefined,
      legalEmail: individual.email || undefined,
      legalAddress: individual.address || undefined,
      legalPhone:
        getFullPhoneNumber(individual.phone.nationalNumber, individual.phone.countryCode) ??
        undefined,
    };
  }

  const { company } = values;
  return {
    legalEntityType: "company",
    legalCompanyName: company.name || undefined,
    registrationNumber: company.registrationNumber || undefined,
    vatNumber: company.vatNumber || undefined,
    legalCountry: company.registrationCountry ?? undefined,
    legalAddress: company.address || undefined,
    legalEmail: company.email || undefined,
    isCurrentUserSigner: false,
    signerName: company.signerName || undefined,
    signerSurname: company.signerSurname || undefined,
    signerRole: company.signerRole || undefined,
    signerEmail: company.signerEmail || undefined,
  };
};

/**
 * Legal fields for the UPDATE DTO: the active branch's fields, opposite-branch
 * fields explicitly cleared with `null`. The signer is always the named person, so
 * `isCurrentUserSigner` is sent as a fixed `false` and the signer* fields are sent.
 */
const mapPlatformLegalToUpdateFields = (
  values: PlatformLegalFormValues
): Partial<DashboardUpdatePlatformInput> => {
  if (values.legalEntityType === "individual") {
    const { individual } = values;
    return {
      legalEntityType: "individual",
      legalName: individual.name || null,
      legalSurname: individual.surname || null,
      legalCountry: individual.residence ?? null,
      personalIdentificationNumber: individual.personalNumber || null,
      legalEmail: individual.email || null,
      legalPhone:
        getFullPhoneNumber(individual.phone.nationalNumber, individual.phone.countryCode) ?? null,
      // Clear the company + signer branch.
      legalCompanyName: null,
      registrationNumber: null,
      vatNumber: null,
      legalAddress: individual.address || null,
      isCurrentUserSigner: false,
      signerName: null,
      signerSurname: null,
      signerRole: null,
      signerEmail: null,
    };
  }

  const { company } = values;
  return {
    legalEntityType: "company",
    legalCompanyName: company.name || null,
    registrationNumber: company.registrationNumber || null,
    vatNumber: company.vatNumber || null,
    legalCountry: company.registrationCountry ?? null,
    legalAddress: company.address || null,
    isCurrentUserSigner: false,
    signerName: company.signerName || null,
    signerSurname: company.signerSurname || null,
    signerRole: company.signerRole || null,
    signerEmail: company.signerEmail || null,
    // Clear the individual branch.
    legalName: null,
    legalSurname: null,
    personalIdentificationNumber: null,
    legalEmail: company.email || null,
    legalPhone: null,
  };
};

// --- inbound: PlatformDetail → per-section form values ----------------------

export const mapPlatformDetailToOwnerFormValues = (
  detail: PlatformDetail
): PlatformOwnerFormValues => ({
  owner: detail.owner,
  dataOwner: detail.dataOwner,
});

export const mapPlatformDetailToGeneralFormValues = (
  detail: PlatformDetail
): PlatformGeneralFormValues => ({
  title: detail.title,
  type: detail.type,
  abbreviation: detail.abbreviation ?? "",
  sports: detail.sports,
});

export const mapPlatformDetailToRegionalFormValues = (
  detail: PlatformDetail
): PlatformRegionalFormValues => ({
  geoScope: detail.geoScope,
  countries: detail.countries,
  languages: detail.languages,
  timeZone: detail.timeZone,
  currencies: detail.currencies,
});

/** Neutral fallback when a platform has no stored brand colour. */
const DEFAULT_BRANDING_COLOR = "#000000";

/**
 * Seed a `Color` for the branding form. Legacy rows may store a bare hex without
 * the leading `#` (e.g. `"bf405f"`); prefix it so `toColor` doesn't choke. Falls
 * back to {@link DEFAULT_BRANDING_COLOR} when null / empty.
 */
const seedBrandingColor = (value: string | null): Color => {
  const hex = (value ?? "").trim().replace(/^#/, "");
  return toColor(hex.length > 0 ? `#${hex}` : DEFAULT_BRANDING_COLOR);
};

export const mapPlatformDetailToBrandingFormValues = (
  detail: PlatformDetail
): PlatformBrandingFormValues => ({
  lightLogo: makeFileUploadFieldState(detail.lightLogoUrl, detail.lightLogoFileId),
  darkLogo: makeFileUploadFieldState(detail.darkLogoUrl, detail.darkLogoFileId),
  lightEmblem: makeFileUploadFieldState(detail.lightEmblemUrl, detail.lightEmblemFileId),
  darkEmblem: makeFileUploadFieldState(detail.darkEmblemUrl, detail.darkEmblemFileId),
  favicon: makeFileUploadFieldState(detail.faviconUrl, detail.faviconFileId),
  useCustomColorPalette: Boolean(detail.primaryColor),
  primaryColor: seedBrandingColor(detail.primaryColor),
});

/**
 * `PlatformDetail` → legal form values. `defaultCountry` seeds the residence /
 * registration-country of whichever branch is NOT active (mirrors the
 * organization legal form's empty-branch defaults).
 */
export const mapPlatformDetailToLegalFormValues = (
  detail: PlatformDetail,
  defaultCountry: CountryCode
): PlatformLegalFormValues => {
  // Seed from the platform's EFFECTIVE legal identity, not its own columns:
  // while `usesEntityLegalDetails` is on the platform stores nothing and the
  // effective values are the entity's, which is exactly what the (read-only)
  // fields must show. Falling back to the detail's own columns covers a platform
  // that stores its own identity on a backend that has not shipped the resolved
  // field yet — and leaves the fields blank when the backend withheld the
  // entity's legal record from this user.
  const legal = detail.resolvedLegalDetails ?? detail;
  const entityType = legal.legalEntityType ?? "individual";

  return {
    usesEntityDetails: detail.usesEntityLegalDetails,
    legalEntityType: entityType,
    individual: {
      name: legal.legalName ?? "",
      surname: legal.legalSurname ?? "",
      residence:
        entityType === "individual" ? (legal.legalCountry ?? defaultCountry) : defaultCountry,
      personalNumber: legal.personalIdentificationNumber ?? "",
      email: legal.legalEmail ?? "",
      address: legal.legalAddress ?? "",
      phone: makePhoneFieldState({ defaultCountry, fullNumber: legal.legalPhone }),
    },
    company: {
      name: legal.legalCompanyName ?? "",
      registrationNumber: legal.registrationNumber ?? "",
      vatNumber: legal.vatNumber ?? "",
      registrationCountry:
        entityType === "company" ? (legal.legalCountry ?? defaultCountry) : defaultCountry,
      address: legal.legalAddress ?? "",
      email: legal.legalEmail ?? "",
      signerRole: legal.signerRole ?? "",
      signerName: legal.signerName ?? "",
      signerSurname: legal.signerSurname ?? "",
      signerEmail: legal.signerEmail ?? "",
    },
  };
};

/** `PlatformDetail` → the Legal documents panels' form values. */
export const mapPlatformDetailToLegalDocumentsFormValues = (
  detail: PlatformDetail
): PlatformLegalDocumentsFormValues => ({
  termsSource: detail.termsSource,
  termsContent: detail.termsContent,
  privacyPolicySource: detail.privacyPolicySource,
  privacyPolicyContent: detail.privacyPolicyContent,
  cookiePolicySource: detail.cookiePolicySource,
  cookiePolicyContent: detail.cookiePolicyContent,
});

// --- inbound: owner federation → platform prefill (CREATE only) -------------
// When a platform is created, its owner federation seeds a few sections the two
// entities share (geographic scope + country, and the full legal-detail set). The
// federation detail carries the same legal field names as `PlatformDetail`, so the
// legal mapper mirrors {@link mapPlatformDetailToLegalFormValues}. The create page
// applies these only into still-untouched fields, so a user's edits are never lost.

/**
 * Owner federation detail → the Regional fields a new platform can inherit from its
 * owner. `geoScope` mirrors the federation's geographic scope; `countries` wraps the
 * owner's single country (empty when the federation has no resolvable country).
 */
export const mapFederationDetailToPlatformRegionalPrefill = (
  detail: DashboardFederationDetail
): Pick<PlatformRegionalFormValues, "geoScope" | "countries"> => {
  const country = detail.country ? mapToCountryCodeNullable(detail.country) : null;
  return {
    geoScope: detail.geographicScope ?? null,
    countries: country ? [country] : [],
  };
};

/**
 * Owner federation detail → platform Legal form values, mirroring
 * {@link mapPlatformDetailToLegalFormValues} (the federation exposes the same legal
 * field set). `defaultCountry` seeds the residence / registration-country of whichever
 * branch is NOT active. Consumed by the create page to prefill an untouched Legal step.
 */
export const mapFederationDetailToPlatformLegalFormValues = (
  detail: DashboardFederationDetail,
  defaultCountry: CountryCode
): PlatformLegalFormValues => {
  const entityType = detail.legalEntityType ?? "individual";
  const legalCountry = detail.legalCountry ? mapToCountryCodeNullable(detail.legalCountry) : null;

  return {
    // The create wizard always stores the new platform's own legal identity —
    // it never renders the inherit switch.
    usesEntityDetails: false,
    legalEntityType: entityType,
    individual: {
      name: detail.legalName ?? "",
      surname: detail.legalSurname ?? "",
      residence: entityType === "individual" ? (legalCountry ?? defaultCountry) : defaultCountry,
      personalNumber: detail.personalIdentificationNumber ?? "",
      email: detail.legalEmail ?? "",
      address: detail.legalAddress ?? "",
      phone: makePhoneFieldState({ defaultCountry, fullNumber: detail.legalPhone }),
    },
    company: {
      name: detail.legalCompanyName ?? "",
      registrationNumber: detail.registrationNumber ?? "",
      vatNumber: detail.vatNumber ?? "",
      registrationCountry:
        entityType === "company" ? (legalCountry ?? defaultCountry) : defaultCountry,
      address: detail.legalAddress ?? "",
      email: detail.legalEmail ?? "",
      signerRole: detail.signerRole ?? "",
      signerName: detail.signerName ?? "",
      signerSurname: detail.signerSurname ?? "",
      signerEmail: detail.signerEmail ?? "",
    },
  };
};

export const mapPlatformDetailToModulesIntegrationsFormValues = (
  detail: PlatformDetail
): PlatformModulesIntegrationsFormValues => ({
  enableToursLeagues: detail.enableToursLeagues,
  enableRankings: detail.enableRankings,
  isChatEnabled: detail.isChatEnabled,
  onlineBookingCourts: detail.onlineBookingCourts,
  licenseMemberships: detail.licenseMemberships,
  liveScoring: detail.liveScoring,
  liveStreaming: detail.liveStreaming,
  ratingSystems: detail.ratingSystems ?? {
    wtn: EMPTY_PLATFORM_RATING_SYSTEM,
    dupr: EMPTY_PLATFORM_RATING_SYSTEM,
    wpr: EMPTY_PLATFORM_RATING_SYSTEM,
  },
  enableThirdPartyLicenses: detail.thirdPartyLicenses.length > 0,
  thirdPartyLicenses: detail.thirdPartyLicenses,
  sporfieMode: detail.sporfieMode ?? "use_platform_key",
  sporfieKey: detail.sporfieKey ?? "",
});

export const mapPlatformDetailToDomainFormValues = (
  detail: PlatformDetail
): PlatformDomainFormValues => ({
  subDomain: detail.subDomain ?? "",
  platformLink: detail.platformLink ?? "",
  landingLink: detail.landingLink ?? "",
});

/**
 * `PlatformDetail` → fees form values (both service-type tabs), prefilled from the
 * persisted rows via the shared FeesForm seeder. `allowedCurrencies` bounds the
 * default-tab currency for a bucket with no rows.
 */
export const mapPlatformDetailToFeesFormValues = (
  detail: PlatformDetail,
  allowedCurrencies: CurrencyCode[]
): PlatformFeesFormValues => ({
  entry: mapFeesSegmentsToFeeTabData("entry", detail.fees.entry, allowedCurrencies),
  license: mapFeesSegmentsToFeeTabData("license", detail.fees.license, allowedCurrencies),
  vat: mapTournatedFeeVatToFormValues(detail.tournatedFeeVat),
});

// --- outbound: CREATE builder -----------------------------------------------

export type BuildCreatePlatformInputSections = {
  owner: PlatformOwnerFormValues;
  general: PlatformGeneralFormValues;
  regional: PlatformRegionalFormValues;
  branding: PlatformBrandingFormValues;
  legal: PlatformLegalFormValues;
  fees: PlatformFeesSubmittedValues;
  modulesIntegrations: PlatformModulesIntegrationsFormValues;
  domain: PlatformDomainFormValues;
  /** Branding storage file ids the page resolved by awaiting its uploads first. */
  brandingFileIds: PlatformBrandingFileIds;
};

/**
 * Compose all wizard sections into the full `DashboardCreatePlatform` POST body.
 * Pure (values + resolved file ids in, DTO out); optional fields are omitted
 * (`undefined`) rather than sent empty.
 */
export const buildCreatePlatformInput = ({
  owner,
  general,
  regional,
  branding,
  legal,
  modulesIntegrations,
  domain,
  brandingFileIds,
}: BuildCreatePlatformInputSections): DashboardCreatePlatform => ({
  // Owner — both refs are required by the wizard schema (submit is blocked until
  // they are chosen), so they are guaranteed present at this call site.
  ownerId: owner.owner!.id,
  dataOwnerId: owner.dataOwner!.id,

  // General — `type` is schema-required; `abbreviation` / `sports` are validated
  // non-empty, so they map straight through as the DTO's required fields.
  title: general.title,
  type: general.type!,
  abbreviation: general.abbreviation,
  sportIds: general.sports.map((sport) => sport.id),

  // Regional — `geoScope` is schema-required; `languages` / `timeZone` are
  // validated non-empty. `countries` stays optional (cleared for global scope).
  geoScope: regional.geoScope!,
  countries: regional.countries.length ? regional.countries : undefined,
  languages: regional.languages,
  timeZone: regional.timeZone,
  currencies: regional.currencies,

  // Branding
  lightLogoFileId: brandingFileIds.lightLogoFileId ?? undefined,
  darkLogoFileId: brandingFileIds.darkLogoFileId ?? undefined,
  lightEmblemFileId: brandingFileIds.lightEmblemFileId ?? undefined,
  darkEmblemFileId: brandingFileIds.darkEmblemFileId ?? undefined,
  faviconFileId: brandingFileIds.faviconFileId ?? undefined,
  ...mapPlatformColorsToCreate(branding),

  // Legal
  ...mapPlatformLegalToCreateFields(legal),

  // Modules & integrations
  enableToursLeagues: modulesIntegrations.enableToursLeagues,
  enableRankings: modulesIntegrations.enableRankings,
  isChatEnabled: modulesIntegrations.isChatEnabled,
  onlineBookingCourts: modulesIntegrations.onlineBookingCourts,
  licenseMemberships: modulesIntegrations.licenseMemberships,
  liveScoring: modulesIntegrations.liveScoring,
  liveStreaming: modulesIntegrations.liveStreaming,
  ratingSystems: mapPlatformRatingSystemsToInput(modulesIntegrations.ratingSystems),
  thirdPartyLicenseIds: modulesIntegrations.enableThirdPartyLicenses
    ? modulesIntegrations.thirdPartyLicenses.map((license) => license.id)
    : [],
  sporfieMode: modulesIntegrations.sporfieMode,
  ...(modulesIntegrations.sporfieMode === "custom_key" && modulesIntegrations.sporfieKey
    ? { liveStreaming_sporfie_key: modulesIntegrations.sporfieKey }
    : {}),

  // Domain
  subDomain: domain.subDomain || undefined,
  platformLink: domain.platformLink || undefined,
  landingLink: domain.landingLink || undefined,
});

// --- outbound: per-section UPDATE (PATCH) slices ----------------------------
// Each returns only its section's fields; `null` clears a nullable-clearable field,
// an omitted field leaves the stored value unchanged.

/** `ownerId` / `dataOwnerId` are not nullable-clearable, so a null ref omits (leaves unchanged). */
export const buildOwnerUpdateSlice = (
  values: PlatformOwnerFormValues
): Partial<DashboardUpdatePlatformInput> => ({
  ownerId: values.owner?.id,
  dataOwnerId: values.dataOwner?.id,
});

export const buildGeneralUpdateSlice = (
  values: PlatformGeneralFormValues
): Partial<DashboardUpdatePlatformInput> => ({
  title: values.title,
  type: values.type ?? undefined,
  abbreviation: values.abbreviation || undefined,
  sportIds: values.sports.map((sport) => sport.id),
});

export const buildRegionalUpdateSlice = (
  values: PlatformRegionalFormValues
): Partial<DashboardUpdatePlatformInput> => ({
  geoScope: values.geoScope ?? undefined,
  countries: values.countries,
  languages: values.languages,
  timeZone: values.timeZone || undefined,
  currencies: values.currencies,
});

export const buildBrandingUpdateSlice = (
  values: PlatformBrandingFormValues,
  fileIds: PlatformBrandingFileIds
): Partial<DashboardUpdatePlatformInput> => ({
  lightLogoFileId: fileIds.lightLogoFileId,
  darkLogoFileId: fileIds.darkLogoFileId,
  lightEmblemFileId: fileIds.lightEmblemFileId,
  darkEmblemFileId: fileIds.darkEmblemFileId,
  faviconFileId: fileIds.faviconFileId,
  ...mapPlatformColorsToUpdate(values),
});

/**
 * The Legal details section's slice — the inherit toggle plus, when the platform
 * stores its own identity, the full legal + signer set.
 *
 * The two are mutually exclusive on the wire: while the toggle is ON the backend
 * clears the platform's own legal columns and **rejects** a body that also
 * carries them (`PLATFORM_LEGAL_DETAILS_MANAGED_BY_ENTITY`), so this sends the
 * flag alone.
 */
export const buildLegalUpdateSlice = (
  values: PlatformLegalFormValues
): Partial<DashboardUpdatePlatformInput> =>
  values.usesEntityDetails
    ? { usesEntityLegalDetails: true }
    : { usesEntityLegalDetails: false, ...mapPlatformLegalToUpdateFields(values) };

/**
 * The Legal documents section's slice. A body is sent only in `custom` mode —
 * `default` clears the stored body server-side, and sending a stale draft
 * alongside it would be rejected as an empty custom document on the next switch.
 */
export const buildLegalDocumentsUpdateSlice = (
  values: PlatformLegalDocumentsFormValues
): Partial<DashboardUpdatePlatformInput> => ({
  termsSource: values.termsSource,
  termsContent: values.termsSource === "custom" ? values.termsContent : null,
  privacyPolicySource: values.privacyPolicySource,
  privacyPolicyContent:
    values.privacyPolicySource === "custom" ? values.privacyPolicyContent : null,
  cookiePolicySource: values.cookiePolicySource,
  cookiePolicyContent: values.cookiePolicySource === "custom" ? values.cookiePolicyContent : null,
});

/**
 * The Fees tab's slice: the fee ladder plus the platform's VAT on the Tournated
 * fee. The VAT keys are sent only by a caller who may set them (`canEditVat` —
 * super admin); anyone else never sends them.
 */
export const buildFeesUpdateSlice = (
  submitted: PlatformFeesSubmittedValues,
  { canEditVat }: { canEditVat: boolean }
): Partial<DashboardUpdatePlatformInput> => ({
  fees: mapPlatformFeesToInput(submitted),
  ...(canEditVat ? mapFeeVatFormValuesToInput(submitted.vat) : {}),
});

export const buildModulesIntegrationsUpdateSlice = (
  values: PlatformModulesIntegrationsFormValues
): Partial<DashboardUpdatePlatformInput> => ({
  enableToursLeagues: values.enableToursLeagues,
  enableRankings: values.enableRankings,
  isChatEnabled: values.isChatEnabled,
  onlineBookingCourts: values.onlineBookingCourts,
  licenseMemberships: values.licenseMemberships,
  liveScoring: values.liveScoring,
  liveStreaming: values.liveStreaming,
  // `ratingSystems` is deliberately OMITTED: the Modules tab renders no
  // rating-system cards, and the prefill falls back to all-off when the stored
  // value is null — whole-object-replacing here silently wiped stored config on
  // every unrelated save of this tab. Omitting the key leaves the stored value
  // untouched (the create path still sends the defaults).
  thirdPartyLicenseIds: values.enableThirdPartyLicenses
    ? values.thirdPartyLicenses.map((license) => license.id)
    : [],
  sporfieMode: values.sporfieMode,
  liveStreaming_sporfie_key: values.sporfieMode === "custom_key" ? values.sporfieKey || null : null,
});

/**
 * A platform Extras switch, tolerated as absent until the generated dashboard
 * client carries it. A non-boolean read (absent / null) falls back to
 * `whenAbsent`, which defaults to off — the same meaning the backend's nullable
 * column has. `enableUserCreation` passes `true` instead: it *disables* an
 * affordance, so a deploy gap must never hide the create-user surfaces.
 */
const readPlatformExtraFlag = (
  source: object,
  key: "enableLTS10" | "enableUspaLicenses" | "enableUserCreation",
  whenAbsent = false
): boolean => {
  const value = (source as Record<string, unknown>)[key];
  return typeof value === "boolean" ? value : whenAbsent;
};

/** `googleAnalyticsId`, tolerated as absent until the generated dashboard client carries it. */
const readPlatformGoogleAnalyticsId = (source: object): string | null => {
  const value = (source as Record<string, unknown>).googleAnalyticsId;
  return typeof value === "string" && value !== "" ? value : null;
};

/**
 * Outbound: the Extras tab's superAdmin-only feature switches. Sent as their own
 * slice so a save from any other tab never carries them — the backend rejects
 * the whole body with 403 when a non-superAdmin includes either key.
 */
export const buildExtrasUpdateSlice = (
  values: PlatformExtrasFormValues
): Partial<DashboardUpdatePlatformInput> =>
  // Cast until `yarn rest:codegen` picks the keys up from the updated
  // platform contract; the backend already accepts (and gates) them.
  ({
    enableLTS10: values.enableLTS10,
    enableUspaLicenses: values.enableUspaLicenses,
    enableUserCreation: values.enableUserCreation,
    googleAnalyticsId: values.googleAnalyticsId.trim() || null,
  }) as Partial<DashboardUpdatePlatformInput>;

/** Inbound: seed the Extras form from a loaded platform. */
export const mapPlatformDetailToExtrasFormValues = (
  detail: PlatformDetail
): PlatformExtrasFormValues => ({
  enableLTS10: detail.enableLTS10,
  enableUspaLicenses: detail.enableUspaLicenses,
  enableUserCreation: detail.enableUserCreation,
  googleAnalyticsId: detail.googleAnalyticsId ?? "",
});

export const buildDomainUpdateSlice = (
  values: PlatformDomainFormValues
): Partial<DashboardUpdatePlatformInput> => ({
  subDomain: values.subDomain || null,
  platformLink: values.platformLink || null,
  landingLink: values.landingLink || null,
});
